import { useEffect, useId, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, MessageSquarePlus } from 'lucide-react';
import {
  formatMeasurementValue,
  isParsableMeasurement,
  type CapturedMeasurementsInput,
  type MeasurementProfile,
  type MeasurementSketchRef,
  type MeasurementTemplate,
  type ResolvedMeasurementField,
} from '@tailoring-crm/shared';
import { isApiRequestError } from '../../lib/api/errors.js';
import {
  useCaptureForm,
  useCreateProfile,
  useUpdateProfile,
  type MeasurementOwner,
} from '../../lib/measurements/use-profiles.js';
import { useMeasurementSketch } from '../../lib/measurements/sketches-api.js';
import { MediaUploader } from '../media/media-uploader.js';
import { SketchField } from './sketch-field.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
  ListSkeleton,
  useToast,
} from '@tailoring-crm/ui';

/**
 * The capture form.
 *
 * Every field on this screen comes from the template the API returned — the order, the section
 * headings, the units, the helper text and which fields exist at all. Nothing about a blouse is
 * written in this file, which is the whole reason a shop can add a field and see it on the next
 * capture without the app shipping again.
 *
 * Everything else is shaped by where it is used: a tailor with a tape measure in one hand and a
 * phone in the other.
 *
 *  - `inputMode="decimal"` puts the numeric keypad up, and entry accepts what a tailor writes —
 *    `36`, `36.5` and `36 1/2` are all read by the same shared parser the server uses;
 *  - a value that looks unlikely says so UNDER the field, in grey, and does not stop the save.
 *    Unusual bodies exist, and a form that refuses one is a form the shop abandons for paper;
 *  - every field can carry a note, because "loose at waist" is a measurement too. The note box
 *    is hidden behind a small button so the form stays a column of numbers until it is needed.
 */

export interface MeasurementCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Whose measurements these are — the customer on her hub. Null
   * on the Add-a-customer form, where the person being measured has no record yet; see
   * `onCaptured`.
   */
  owner?: MeasurementOwner | null;
  /** The garment being measured. Fixed for an edit; chosen by the caller for a new capture. */
  garmentTypeKey: string;
  /** Present when editing an existing profile. */
  profile?: MeasurementProfile | null;
  /** Present when duplicating: values are pre-filled from it, and it is sent as the source. */
  duplicateOf?: MeasurementProfile | null;
  /**
   * DRAFT MODE. Given instead of an owner: the dialog gathers the same reading and hands it back
   * rather than saving it, because the customer it belongs to does not exist yet. The Add-a-
   * customer form holds the result and posts it with her, in one transaction.
   *
   * The form below is identical either way — same template, same fields, same parser — which is
   * the point of routing only the LAST step differently. What draft mode cannot show is the
   * server's plausibility warnings, since nothing has reached the server; they are computed and
   * stored when the customer is created, and appear on her page.
   */
  onCaptured?: (captured: CapturedMeasurementsInput) => void;
  /** A draft being corrected: seeds the form the way `profile` seeds it for a real edit. */
  draft?: CapturedMeasurementsInput | null;
  onSaved?: (profile: MeasurementProfile) => void;
}

interface Entry {
  enteredAs: string;
  note: string;
}

/** A draft's value, shown back exactly as the counter typed it — nothing canonicalised it. */
function draftEntryFor(draft: CapturedMeasurementsInput, fieldKey: string): Entry {
  const value = draft.values?.find((one) => one.fieldKey === fieldKey);
  return { enteredAs: value?.enteredAs ?? '', note: value?.note ?? '' };
}

/** How a stored value is shown back in the input: what was typed, else the canonical form. */
function entryFor(profile: MeasurementProfile | null | undefined, fieldKey: string): Entry {
  const value = profile?.values.find((one) => one.fieldKey === fieldKey);
  if (!value) return { enteredAs: '', note: '' };
  const shown =
    value.enteredAs ??
    (value.value !== null
      ? formatMeasurementValue(value.value, { unicode: false })
      : (value.text ?? ''));
  return { enteredAs: shown, note: value.note ?? '' };
}

/** Consecutive runs under one heading, exactly as the template orders them. */
function sectionsOf(
  fields: readonly ResolvedMeasurementField[],
): { group: string | null; fields: ResolvedMeasurementField[] }[] {
  const sections: { group: string | null; fields: ResolvedMeasurementField[] }[] = [];
  for (const field of fields) {
    const last = sections.at(-1);
    if (last && last.group === (field.group ?? null)) last.fields.push(field);
    else sections.push({ group: field.group ?? null, fields: [field] });
  }
  return sections;
}

export function MeasurementCaptureDialog({
  open,
  onOpenChange,
  owner = null,
  garmentTypeKey,
  profile = null,
  duplicateOf = null,
  onCaptured,
  draft = null,
  onSaved,
}: MeasurementCaptureDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fieldId = useId();

  const isEdit = profile !== null;
  const source = profile ?? duplicateOf;

  const form = useCaptureForm(open ? garmentTypeKey : null, profile?.id);
  const create = useCreateProfile();
  const update = useUpdateProfile(profile?.id ?? '');

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [openNotes, setOpenNotes] = useState<Set<string>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  /** The profile as saved, so its warnings can be shown before the dialog closes. */
  const [saved, setSaved] = useState<MeasurementProfile | null>(null);
  /** The drawing, held until Save like every other part of the form. */
  const [sketch, setSketch] = useState<MeasurementSketchRef | null>(null);
  /** A draft carries only its sketch's id; the picture to show is read off the sketch. */
  const draftSketch = useMeasurementSketch(open && draft?.sketchId ? draft.sketchId : null);
  const draftPreviewMediaId = draftSketch.data?.previewMediaId ?? null;

  const fields = useMemo(() => form.data?.fields ?? [], [form.data]);
  const template: MeasurementTemplate | undefined = form.data?.template;

  useEffect(() => {
    if (!open) return;
    setName(profile?.name ?? draft?.name ?? '');
    setNotes(source?.notes ?? draft?.notes ?? '');
    setFormError(null);
    setMissing([]);
    setSaved(null);
    setOpenNotes(
      new Set(source?.values.filter((value) => value.note).map((value) => value.fieldKey)),
    );
    setSketch(
      source?.sketch ??
        (draft?.sketchId ? { sketchId: draft.sketchId, previewMediaId: null } : null),
    );
  }, [open, profile, duplicateOf, source, draft]);

  // A draft's picture arrives after the form opens; fill it in unless it has been redrawn since.
  useEffect(() => {
    if (!draftPreviewMediaId) return;
    setSketch((current) =>
      current && current.sketchId === draft?.sketchId
        ? { ...current, previewMediaId: draftPreviewMediaId }
        : current,
    );
  }, [draftPreviewMediaId, draft?.sketchId]);

  // Values wait for the form, because a field's entry cannot be prepared before its key is known.
  useEffect(() => {
    if (!open || fields.length === 0) return;
    setEntries(
      Object.fromEntries(
        fields.map((field) => [
          field.key,
          draft ? draftEntryFor(draft, field.key) : entryFor(source, field.key),
        ]),
      ),
    );
  }, [open, fields, source, draft]);

  const set = (key: string, patch: Partial<Entry>): void => {
    setEntries((previous) => ({
      ...previous,
      [key]: { enteredAs: '', note: '', ...previous[key], ...patch },
    }));
  };

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    /*
     * And stop it going any further. This dialog is portalled into the body, but React routes
     * events up the COMPONENT tree, not the DOM one — so on the Add-a-customer screen, where
     * the capture dialog is rendered inside the customer form, submitting a set of measurements
     * would also submit the form behind it. That is not a cosmetic bug: a form filled in enough
     * to pass validation would have created the customer the moment somebody finished measuring
     * her, which is the exact thing this whole flow exists to stop.
     */
    event.stopPropagation();
    setFormError(null);

    if (name.trim() === '') {
      setFormError(t('profiles.nameLabel'));
      return;
    }

    // Required fields are named rather than merely highlighted, so the owner knows what is
    // left without scrolling a long form hunting for red.
    const unanswered = fields.filter(
      (field) => field.required && (entries[field.key]?.enteredAs ?? '').trim() === '',
    );
    if (unanswered.length > 0) {
      setMissing(unanswered.map((field) => field.label));
      return;
    }
    setMissing([]);

    const values = fields
      .map((field) => ({
        fieldKey: field.key,
        enteredAs: entries[field.key]?.enteredAs.trim() ?? '',
        note: entries[field.key]?.note.trim() ?? '',
      }))
      .filter((value) => value.enteredAs !== '' || value.note !== '');

    // Draft mode: hand the reading back and close. Nothing is posted, so there is nothing to
    // fail, nothing to toast and no warnings to hold the dialog open for — the customer's own
    // create is where all three happen.
    if (onCaptured) {
      onCaptured({
        garmentTypeKey,
        name,
        notes: notes.trim() || null,
        values,
        sketchId: sketch?.sketchId ?? null,
      });
      onOpenChange(false);
      return;
    }

    // Past here there is somebody to save against. One of `owner` and `onCaptured` is always
    // given — every caller is above, in this repo — so this is a guard against a future one
    // rather than a case the counter can reach.
    if (!owner) {
      setFormError(t('error.unexpected'));
      return;
    }

    try {
      const result = isEdit
        ? await update.mutateAsync({
            name,
            notes: notes.trim() || null,
            values,
            // Only a CHANGED drawing is sent: an untouched one is not a redraw.
            ...(sketch?.sketchId !== profile.sketch?.sketchId
              ? { sketchId: sketch?.sketchId ?? null }
              : {}),
          })
        : await create.mutateAsync({
            customerId: owner.customerId,
            garmentTypeKey,
            name,
            notes: notes.trim() || null,
            values,
            sketchId: sketch?.sketchId ?? null,
            ...(duplicateOf ? { duplicateOfProfileId: duplicateOf.id } : {}),
          });

      toast({ title: t('profiles.saved'), variant: 'success' });
      onSaved?.(result);

      // A save that produced warnings stays open so the shop reads them; a clean save closes.
      if (result.warnings.length > 0) setSaved(result);
      else onOpenChange(false);
    } catch (error) {
      setFormError(
        isApiRequestError(error) || error instanceof Error ? error.message : t('error.unexpected'),
      );
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('profiles.editTitle', { name: profile.name })
              : duplicateOf
                ? t('profiles.duplicateTitle', { name: duplicateOf.name })
                : t('profiles.captureTitle')}
          </DialogTitle>
          <DialogDescription>
            {duplicateOf ? t('profiles.duplicateHint') : t('profiles.entryHint')}
          </DialogDescription>
        </DialogHeader>

        {form.isPending ? (
          <ListSkeleton rows={4} />
        ) : (
          <form className="flex flex-col gap-5" onSubmit={(event) => void submit(event)} noValidate>
            {formError ? (
              <p
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
              >
                {formError}
              </p>
            ) : null}

            {missing.length > 0 ? (
              <p
                role="alert"
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
              >
                {t('profiles.missingRequired', { fields: missing.join(', ') })}
              </p>
            ) : null}

            {/* Warnings from the save that just happened: the values are already stored. */}
            {saved && saved.warnings.length > 0 ? (
              <div
                role="status"
                className="flex flex-col gap-1 rounded-md border border-warning/40 bg-warning/10 px-3 py-3"
              >
                <p className="flex items-center gap-2 text-sm font-semibold">
                  <AlertTriangle aria-hidden="true" className="size-4" />
                  {t('profiles.warningsTitle')}
                </p>
                <ul className="list-disc pl-5 text-sm">
                  {saved.warnings.map((warning) => (
                    <li key={`${warning.fieldKey}-${warning.reason}`}>{warning.message}</li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground">{t('profiles.warningsKept')}</p>
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-name`}>{t('profiles.nameLabel')}</Label>
              <Input
                id={`${fieldId}-name`}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t('profiles.namePlaceholder')}
              />
              <p className="text-sm text-muted-foreground">{t('profiles.nameHint')}</p>
            </div>

            {sectionsOf(fields).map((section, index) => (
              <section
                key={`${section.group ?? 'general'}-${index}`}
                className="flex flex-col gap-3"
              >
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {section.group ?? t('profiles.ungrouped')}
                </h4>

                {section.fields.map((field) => {
                  const entry = entries[field.key] ?? { enteredAs: '', note: '' };
                  const typed = entry.enteredAs.trim();
                  // Read with the SAME parser the server uses, so the preview never disagrees
                  // with what gets stored.
                  const readable = typed === '' || isParsableMeasurement(typed);
                  const showNote = openNotes.has(field.key);

                  return (
                    <div key={field.key} className="flex flex-col gap-1.5">
                      <Label htmlFor={`${fieldId}-${field.key}`}>
                        {field.label}
                        <span className="ml-1 font-normal text-muted-foreground">
                          ({t(`measurements.unit.${field.resolvedUnit}Short`)})
                        </span>
                        {field.required ? (
                          <span className="ml-1 text-destructive" aria-hidden="true">
                            *
                          </span>
                        ) : null}
                        {!field.active ? (
                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                            {t('measurements.inactiveBadge')}
                          </span>
                        ) : null}
                      </Label>

                      <div className="flex gap-2">
                        {field.inputType === 'select' ? (
                          <select
                            id={`${fieldId}-${field.key}`}
                            value={entry.enteredAs}
                            onChange={(event) => set(field.key, { enteredAs: event.target.value })}
                            className="h-11 flex-1 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                          >
                            <option value="" />
                            {field.options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            id={`${fieldId}-${field.key}`}
                            value={entry.enteredAs}
                            onChange={(event) => set(field.key, { enteredAs: event.target.value })}
                            // The numeric keypad, but not `type="number"` — that would reject
                            // "36 1/2", which is exactly how a tailor writes it.
                            {...(field.inputType === 'number'
                              ? { inputMode: 'decimal' as const }
                              : {})}
                            className="flex-1"
                          />
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          // A different accessible name from the note input it reveals, so a
                          // screen reader (and a test) can tell the two apart.
                          aria-label={t('profiles.toggleNote', { label: field.label })}
                          onClick={() =>
                            setOpenNotes((previous) => {
                              const next = new Set(previous);
                              if (next.has(field.key)) next.delete(field.key);
                              else next.add(field.key);
                              return next;
                            })
                          }
                        >
                          <MessageSquarePlus aria-hidden="true" />
                        </Button>
                      </div>

                      {showNote ? (
                        <Input
                          aria-label={t('profiles.fieldNoteLabel', { label: field.label })}
                          value={entry.note}
                          onChange={(event) => set(field.key, { note: event.target.value })}
                        />
                      ) : null}

                      {field.helperText ? (
                        <p className="text-sm text-muted-foreground">{field.helperText}</p>
                      ) : null}
                      {!readable ? (
                        <p className="text-sm text-muted-foreground">{t('profiles.entryHint')}</p>
                      ) : null}
                    </div>
                  );
                })}
              </section>
            ))}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${fieldId}-notes`}>{t('profiles.notesLabel')}</Label>
              <Textarea
                id={`${fieldId}-notes`}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </div>

            <SketchField
              garmentTypeKey={garmentTypeKey}
              name={name.trim() || (template?.garmentTypeLabel ?? garmentTypeKey)}
              value={sketch}
              onChange={setSketch}
              outline={template?.outline ?? null}
              disabled={pending}
            />

            {/* Reference photos, through the shared uploader from task 11 — the same component
                orders and expenses use, differing only in the entityType. */}
            {isEdit ? (
              <div className="flex flex-col gap-1.5">
                <Label>{t('profiles.photosHeading')}</Label>
                <p className="text-sm text-muted-foreground">{t('profiles.photosHint')}</p>
                <MediaUploader
                  entityType="measurement_profile"
                  entityId={profile.id}
                  kind="reference"
                  maxFiles={6}
                />
              </div>
            ) : null}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={pending} data-tour="measure-save">
                {t('common.save')}
              </Button>
            </DialogFooter>

            {template ? <input type="hidden" value={template.garmentTypeKey} readOnly /> : null}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
